/**
 * Shop Now Button Component
 * Handles direct checkout functionality with AJAX add-to-cart
 */

if (!customElements.get('shop-now-button')) {
  customElements.define('shop-now-button', class ShopNowButton extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('[data-shop-now-trigger]');
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.sectionId = this.dataset.sectionId;
      this.buttonLabel = this.dataset.buttonLabel || 'Shop Now';
      this.desktopButtonLabel = this.dataset.desktopButtonLabel || this.buttonLabel;
      this.currentPrice = this.dataset.currentPrice;

      if (this.button) {
        this.button.addEventListener('click', this.handleShopNow.bind(this));
      }

      // Initialize button text
      this.updateButtonText(this.buttonLabel);

      // Listen for variant changes from product form
      this.initVariantChangeListener();
    }

    initVariantChangeListener() {
      // Listen for variant changes in the main product form
      const productForm = document.querySelector(`[data-section-id="${this.sectionId}"] product-form`);
      if (productForm) {
        productForm.addEventListener('variant:change', (event) => {
          this.updateVariant(event.detail.variant);
        });
      }

      // Also listen for variant changes on the document level
      document.addEventListener('variant:change', (event) => {
        if (event.detail.sectionId === this.sectionId) {
          this.updateVariant(event.detail.variant);
        }
      });
    }

    updateVariant(variant) {
      if (!variant) return;

      this.variantId = variant.id;
      this.dataset.variantId = variant.id;

      // Update price from variant
      if (variant.price !== undefined) {
        // Format price according to store settings
        this.currentPrice = this.formatPrice(variant.price);
        this.dataset.currentPrice = this.currentPrice;
      }

      // Update button state based on availability
      if (variant.available) {
        this.button.disabled = false;
        this.updateButtonText(this.buttonLabel);
      } else {
        this.button.disabled = true;
        this.updateButtonText(window.variantStrings?.soldOut || 'Sold out');
      }
    }

    updateButtonText(text) {
      const mobileTextElement = this.button.querySelector('.mobile-button-text');
      const desktopTextElement = this.button.querySelector('.desktop-button-text');

      if (mobileTextElement && desktopTextElement) {
        // Update mobile text (always just the text)
        mobileTextElement.textContent = text;

        // Update desktop text with price prepending logic
        let desktopText;
        if (text === this.buttonLabel) {
          // Normal state: use desktop label and check for price prepending
          desktopText = this.desktopButtonLabel;

          // Prepend price on desktop if available and not an error state
          if (this.currentPrice &&
              text !== 'Sold out' &&
              !text.toLowerCase().includes('error')) {
            desktopText = `${this.currentPrice} - ${desktopText}`;
          }
        } else {
          // Error states (sold out, etc.): show same text as mobile
          desktopText = text;
        }

        desktopTextElement.textContent = desktopText;
      } else {
        // Fallback for buttons without responsive text structure
        const buttonText = this.button.querySelector('.btn--text');
        if (buttonText) {
          // Apply original price prepending logic for legacy buttons
          if (window.matchMedia('(min-width: 750px)').matches &&
              this.currentPrice &&
              text !== 'Sold out' &&
              !text.toLowerCase().includes('error')) {
            buttonText.textContent = `${this.currentPrice} - ${text}`;
          } else {
            buttonText.textContent = text;
          }
        }
      }
    }

    formatPrice(priceInCents) {
      // Check if store uses currency codes (from global Shopify theme settings)
      const useCurrencyCode = window.Shopify && window.Shopify.money_format &&
                             window.Shopify.money_format.includes('{{amount_with_currency}}');

      // Format price using Shopify's money formatting
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(priceInCents,
               useCurrencyCode ? window.Shopify.money_with_currency_format : window.Shopify.money_format);
      }

      // Fallback formatting
      const amount = (priceInCents / 100).toFixed(2);
      return useCurrencyCode ? `$${amount} USD` : `$${amount}`;
    }

    showLoading() {
      this.button.classList.add('loading');
      this.button.disabled = true;
    }

    hideLoading() {
      this.button.classList.remove('loading');
      this.button.disabled = false;
    }

    async handleShopNow(event) {
      event.preventDefault();

      if (!this.variantId) {
        console.error('No variant selected');
        return;
      }

      this.showLoading();

      try {
        // Get quantity from quantity selector if present
        const quantitySelector = document.querySelector(`[data-section-id="${this.sectionId}"] [name="quantity"]`);
        const quantity = quantitySelector ? parseInt(quantitySelector.value) || 1 : 1;

        // Add product to cart
        const response = await this.addToCart(this.variantId, quantity);

        if (response.ok) {
          // Success - redirect to checkout
          window.location.href = '/checkout';
        } else {
          // Handle error
          const errorData = await response.json();
          this.handleError(errorData.message || errorData.description || 'Unable to add item to cart');
        }
      } catch (error) {
        console.error('Shop Now error:', error);
        this.handleError('Something went wrong. Please try again.');
      } finally {
        this.hideLoading();
      }
    }

    async addToCart(variantId, quantity = 1) {
      const formData = {
        items: [{
          id: parseInt(variantId, 10),
          quantity: quantity
        }]
      };

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });
    }

    handleError(message) {
      // Show error message
      this.showErrorMessage(message);

      // Hide error after 5 seconds
      setTimeout(() => {
        this.hideErrorMessage();
      }, 5000);
    }

    showErrorMessage(message) {
      // Remove existing error message
      this.hideErrorMessage();

      const errorDiv = document.createElement('div');
      errorDiv.className = 'shop-now-error-message';
      errorDiv.style.cssText = `
        color: #d72c0d;
        background-color: #fbeae7;
        border: 1px solid #d72c0d;
        padding: 8px 12px;
        margin-top: 8px;
        border-radius: 4px;
        font-size: 14px;
        text-align: center;
      `;
      errorDiv.textContent = message;

      this.appendChild(errorDiv);
    }

    hideErrorMessage() {
      const errorMessage = this.querySelector('.shop-now-error-message');
      if (errorMessage) {
        errorMessage.remove();
      }
    }
  });
}

// Initialize shop now buttons when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Initialize any existing shop-now-button elements
  const shopNowButtons = document.querySelectorAll('shop-now-button');
  shopNowButtons.forEach(button => {
    if (!button.initialized) {
      button.initialized = true;
    }
  });
});

// Handle theme editor updates
document.addEventListener('shopify:section:load', function() {
  // Re-initialize shop now buttons after section reload
  const shopNowButtons = document.querySelectorAll('shop-now-button');
  shopNowButtons.forEach(button => {
    if (!button.initialized) {
      button.initialized = true;
    }
  });
});